import produce from "immer";
import { Interval } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import {
  Dataviz,
  getDefaultDataviz,
  isBubbleChartDataviz,
} from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { isGeoTerm } from "@radarly/models/search";
import {
  isAICardDataviz,
  isCardDataviz,
  isClassificationCardDataviz,
  isClusterCardDataviz,
  isDateMetricCardDataviz,
  isDateMetricsCardDataviz,
  isDateTermCardDataviz,
  isDemographicCardDataviz,
  isGeoCardDataviz,
  isPivotCardDataviz,
  isPostsCardDataviz,
  isTermCardDataviz,
  isTermMetricsCardDataviz,
  isUsersCardDataviz,
} from "../dataviz";
import {
  isClassificationCard,
  isDateMetricCard,
  isDateMetricsCard,
  isDateTermCard,
  isDemographicCard,
  isEmvCard,
  isGeoCard,
  isPinnedAuthorCard,
  isPinnedPostCard,
  isPivotCard,
  isTermCard,
  isTermMetricsCard,
  isTimeUnitCard,
} from "../is";
import { Card } from "../model";
import { ComparisonPeriod } from "../period";
import {
  CardTerm,
  PivotCardTerm,
  getTermFromCard,
  isDateTermCardTerm,
  isPivotCardTerm,
  isTermMetricsCardTerm,
} from "../term";
import { AICardFactory } from "./ai";
import { ClassificationCardFactory } from "./classification";
import { ClusterCardFactory } from "./cluster";
import { DateMetricCardFactory } from "./date.metric";
import { DateMetricsCardFactory } from "./date.metrics";
import { DateTermCardFactory } from "./date.term";
import { DemographicCardFactory } from "./demographic";
import { EmvCardFactory } from "./emv";
import { GeoCardFactory } from "./geo";
import { PinnedAuthorCardFactory } from "./pinned.author";
import { PinnedPostCardFactory } from "./pinned.post";
import { PivotCardFactory } from "./pivot";
import { PostsCardFactory } from "./posts";
import { TermCardFactory } from "./term";
import { TermMetricsCardFactory } from "./term.metrics";
import { TimeUnit } from "./time-unit";
import { UsersCardFactory } from "./users";

export class CardFacade<C extends Card> {
  public setCardComparaisonPeriod(
    workspaceId: number,
    projectFocuses: number[],
    card: C,
    comparisonPeriod: ComparisonPeriod
  ): C {
    if (isGeoCard(card)) {
      return GeoCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          comparisonPeriod,
        }
      ) as C;
    } else if (isDateMetricCard(card)) {
      return DateMetricCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          comparisonPeriod,
        }
      ) as C;
    } else if (isClassificationCard(card)) {
      return ClassificationCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          comparisonPeriod,
        }
      ) as C;
    } else if (isTermCard(card)) {
      return TermCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          comparisonPeriod,
        }
      ) as C;
    } else if (isEmvCard(card)) {
      return EmvCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          comparisonPeriod,
        }
      ) as C;
    }
    return card;
  }
  public setCardTimeUnit(card: C, timeUnit: TimeUnit) {
    if (isTimeUnitCard(card)) {
      return produce(card, (draft) => {
        draft.dataviz.timeUnit = timeUnit;
        draft.fragments[0].request.aggs.date.interval =
          timeUnit === "default" ? Interval.Day : timeUnit;
      });
    }
    return;
  }
  public setCardTertiaryMetric(card: C, metric: Metric) {
    if (isTermMetricsCard(card)) {
      return produce(card, (draft) => {
        const term = getTermFromCard(draft as Card);
        draft.fragments[0].request.aggs[term].metrics[1] = metric;
      });
    }
    return;
  }
  public setCardSecondaryMetric(card: C, metric: Metric) {
    if (isDateMetricsCard(card)) {
      return produce(card, (draft) => {
        draft.fragments[0].request.aggs.date.metrics[1] = metric;
      }) as C;
    } else if (isTermMetricsCard(card)) {
      if (isBubbleChartDataviz(card.dataviz)) {
        return produce(card, (draft) => {
          const term = getTermFromCard(draft as Card);
          draft.fragments[0].request.aggs[term].metrics[0] = metric;
        });
      }
      return produce(card, (draft) => {
        const term = getTermFromCard(draft as Card);
        draft.fragments[0].request.aggs[term].metrics[1] = metric;
      });
    }
  }
  public setCardPrimaryMetric(
    workspaceId: number,
    projectFocuses: number[],
    card: C,
    metric: Metric
  ): C {
    if (isTermCard(card)) {
      return TermCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          metric,
        }
      ) as C;
    } else if (isGeoCard(card)) {
      return GeoCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          metric,
        }
      ) as C;
    } else if (isDemographicCard(card)) {
      return DemographicCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          metric,
        }
      ) as C;
    } else if (isClassificationCard(card)) {
      return ClassificationCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          metric,
        }
      ) as C;
    } else if (isTermMetricsCard(card)) {
      if (isBubbleChartDataviz(card.dataviz)) {
        return produce(card, (draft) => {
          const term = getTermFromCard(draft as Card);
          draft.fragments[0].request.aggs[term].metrics[2] = metric;
        });
      }
      return produce(card, (draft) => {
        const term = getTermFromCard(draft as Card);
        draft.fragments[0].request.aggs[term].metrics[0] = metric;
      });
    } else if (isDateMetricsCard(card)) {
      return produce(card, (draft) => {
        draft.fragments[0].request.aggs.date.metrics[0] = metric;
      }) as C;
    } else if (isDateMetricCard(card)) {
      return DateMetricCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          metric,
        }
      ) as C;
    } else if (isDateTermCard(card)) {
      return DateTermCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          metric,
        }
      ) as C;
    } else if (isPivotCard(card)) {
      return PivotCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          metric,
        }
      ) as C;
    }
    return card;
  }

  setCardPivotTerm(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    nextTerm: CardTerm,
    facets: Record<string, FullFacet>
  ) {
    if (isPivotCard(card) && isPivotCardTerm(nextTerm, facets[nextTerm])) {
      return PivotCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          pivot: nextTerm as PivotCardTerm,
        }
      );
    } else {
      return card;
    }
  }

  setCardTerm(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    nextTerm: CardTerm,
    facets: Record<string, FullFacet>
  ) {
    if (getTermFromCard(card) === nextTerm) {
      return card;
    }
    if (isPivotCard(card) && isPivotCardTerm(nextTerm, facets[nextTerm])) {
      return PivotCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          term: nextTerm,
        }
      );
    }
    if (isGeoTerm(nextTerm)) {
      return GeoCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          term: nextTerm,
        }
      );
    }
    if (nextTerm === Facet.ContentClassification) {
      return ClassificationCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card
      );
    }
    if (
      isDateTermCard(card) &&
      isDateTermCardTerm(nextTerm, facets[nextTerm])
    ) {
      return DateTermCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          term: nextTerm,
        }
      );
    }
    if (
      isTermMetricsCard(card) &&
      isTermMetricsCardTerm(nextTerm, facets[nextTerm])
    ) {
      return TermMetricsCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        {
          term: nextTerm,
        }
      );
    }
    return TermCardFactory.getInstance().fromCard(
      workspaceId,
      projectFocuses,
      card,
      {
        term: nextTerm,
      }
    );
  }
  setCardDataviz(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    dataviz: Dataviz["name"]
  ) {
    const nextDataviz = getDefaultDataviz(dataviz);
    // Same Card => Can change dataviz
    if (!isPostsCardDataviz(nextDataviz) && isCardDataviz(card, nextDataviz)) {
      return produce(card, (draft) => {
        draft.dataviz = nextDataviz;
      });
    } else {
      if (isTermCardDataviz(nextDataviz)) {
        return TermCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isGeoCardDataviz(nextDataviz)) {
        return GeoCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isDemographicCardDataviz(nextDataviz)) {
        return DemographicCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isClassificationCardDataviz(nextDataviz)) {
        return ClassificationCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isUsersCardDataviz(nextDataviz)) {
        return UsersCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isTermMetricsCardDataviz(nextDataviz)) {
        return TermMetricsCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isDateMetricsCardDataviz(nextDataviz)) {
        return DateMetricsCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isDateMetricCardDataviz(nextDataviz)) {
        return DateMetricCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isDateTermCardDataviz(nextDataviz)) {
        return DateTermCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isPivotCardDataviz(nextDataviz)) {
        return PivotCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isPostsCardDataviz(nextDataviz)) {
        return PostsCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      } else if (isClusterCardDataviz(nextDataviz)) {
        return ClusterCardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      }
      // @TO-DO: double-check for dataviz
      else if (isAICardDataviz(nextDataviz)) {
        return AICardFactory.getInstance().fromCard(
          workspaceId,
          projectFocuses,
          card,
          {
            dataviz: nextDataviz,
          }
        );
      }
    }
    return card;
  }

  public setCardPostId(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    postId: string
  ) {
    if (isPinnedPostCard(card)) {
      return PinnedPostCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        { dataviz: { ...card.dataviz, postId }, postId }
      );
    }
  }
  public setCardAuthorId(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    authorId: string
  ) {
    if (isPinnedAuthorCard(card)) {
      return PinnedAuthorCardFactory.getInstance().fromCard(
        workspaceId,
        projectFocuses,
        card,
        { dataviz: { ...card.dataviz, authorId }, authorId }
      );
    }
  }
}
